// src/pages/admin/AdminDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { sectionTemplates } from "../../data/database";
import { slugify, isReservedSlug } from "../../data/pagesStore";
import { pagesApi } from "../../api/pagesApi";
import { LogOut } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ConfirmDeleteModal from "../../components/GeneralComponents/ConfirmDeleteModal";
import InfoDialog from "../../components/GeneralComponents/InfoDialog";
import AdminLanguage from "./AdminLanguage";
const deepClone = (obj) => JSON.parse(JSON.stringify(obj || {}));

const SECTION_TYPES = [
  { type: "hero", name: "Hero", icon: "🎯", color: "bg-blue-50 text-blue-600" },
  { type: "features", name: "Features", icon: "⚡", color: "bg-amber-50 text-amber-600" },
  { type: "testimonials", name: "Testimonials", icon: "💬", color: "bg-green-50 text-green-600" },
  { type: "gallery", name: "Gallery", icon: "🖼️", color: "bg-purple-50 text-purple-600" },
  { type: "cta", name: "CTA", icon: "🎯", color: "bg-red-50 text-red-600" },
  { type: "categorysection", name: "Category Section", icon: "📦", color: "bg-indigo-50 text-indigo-600" },
  { type: "locationsection", name: "Location", icon: "📍", color: "bg-emerald-50 text-emerald-600" },
  { type: "serviceSection", name: "Service", icon: "⚙️", color: "bg-orange-50 text-orange-600" },
];

function readActive(page) {
  return typeof page?.active === "boolean" ? page.active : true;
}
function readVisibleRaw(page) {
  return typeof page?.visible === "boolean" ? page.visible : true;
}
function readVisible(page) {
  return readActive(page) && readVisibleRaw(page);
}

function StatCard({ title, value, hint, icon, trend, color = "gray" }) {
  const colorClasses = {
    gray: "from-gray-50 to-gray-100/50 border-gray-200",
    blue: "from-blue-50 to-blue-100/50 border-blue-200",
    green: "from-green-50 to-green-100/50 border-green-200",
    amber: "from-amber-50 to-amber-100/50 border-amber-200",
  };

  return (
    <div
      className={`group relative rounded-2xl border bg-gradient-to-br ${colorClasses[color]} p-6 shadow-sm hover:shadow-md transition-all duration-200`}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold tracking-tight text-gray-900">{value}</p>
          {hint && <p className="text-xs text-gray-500">{hint}</p>}
        </div>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/80 backdrop-blur-sm shadow-sm border border-gray-200 text-2xl group-hover:scale-110 transition-transform">
          {icon}
        </div>
      </div>

      {trend && (
        <div className="mt-4 flex items-center gap-1 text-xs font-medium">
          <span className={trend > 0 ? "text-green-600" : "text-red-600"}>
            {trend > 0 ? "↑" : "↓"} {Math.abs(trend)}%
          </span>
          <span className="text-gray-500">vs last month</span>
        </div>
      )}
    </div>
  );
}

function Badge({ variant = "soft", size = "md", children }) {
  const variants = {
    solid: {
      gray: "bg-gray-900 text-white border-gray-900",
      green: "bg-green-600 text-white border-green-600",
      amber: "bg-amber-600 text-white border-amber-600",
      red: "bg-red-600 text-white border-red-600",
      blue: "bg-blue-600 text-white border-blue-600",
    },
    soft: {
      gray: "bg-gray-100 text-gray-700 border-gray-200",
      green: "bg-green-50 text-green-700 border-green-200",
      amber: "bg-amber-50 text-amber-700 border-amber-200",
      red: "bg-red-50 text-red-700 border-red-200",
      blue: "bg-blue-50 text-blue-700 border-blue-200",
    },
    outline: {
      gray: "bg-transparent text-gray-700 border-gray-300",
      green: "bg-transparent text-green-700 border-green-300",
      amber: "bg-transparent text-amber-700 border-amber-300",
      red: "bg-transparent text-red-700 border-red-300",
      blue: "bg-transparent text-blue-700 border-blue-300",
    },
  };

  const sizeClasses = {
    sm: "px-2 py-0.5 text-xs",
    md: "px-2.5 py-1 text-xs",
    lg: "px-3 py-1.5 text-sm",
  };

  const [variantType, color] = variant.split("-");
  const variantStyle = variants[variantType]?.[color] || variants.soft.gray;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium border ${variantStyle} ${sizeClasses[size]}`}
    >
      {children}
    </span>
  );
}

function Button({
  children,
  variant = "primary",
  size = "md",
  icon,
  fullWidth = false,
  className = "",
  ...props
}) {
  const variants = {
    primary: "bg-gray-900 text-white border-gray-900 hover:bg-gray-800 shadow-sm",
    secondary: "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300 shadow-sm",
    danger: "bg-red-600 text-white border-red-600 hover:bg-red-700",
    ghost: "bg-transparent text-gray-600 border-transparent hover:bg-gray-100",
  };

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm gap-1.5",
    md: "px-4 py-2 text-sm gap-2",
    lg: "px-6 py-3 text-base gap-2",
  };

  const iconClass =
    size === "sm" ? "text-base" : size === "lg" ? "text-xl" : "text-lg";

  return (
    <button
      {...props}
      className={`
        inline-flex items-center justify-center rounded-xl border font-medium
        transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed
        ${variants[variant]} ${sizeClasses[size]} ${fullWidth ? "w-full" : ""} ${className}
      `}
    >
      {icon && <span className={iconClass}>{icon}</span>}
      {children}
    </button>
  );
}

function Switch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200
        border-2 focus:outline-none focus:ring-2 focus:ring-gray-900/20
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        ${checked ? "bg-gray-900 border-gray-900" : "bg-gray-200 border-gray-200"}
      `}
      role="switch"
      aria-checked={checked}
    >
      <span
        className={`
          inline-block h-5 w-5 transform rounded-full bg-white shadow-sm
          transition-transform duration-200 ease-in-out
          ${checked ? "translate-x-5" : "translate-x-0"}
        `}
      />
    </button>
  );
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl
                 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300
                 placeholder:text-gray-400 transition-all"
      />
    </div>
  );
}

function Select({ value, onChange, options, className = "" }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className={`
        px-4 py-2.5 border border-gray-200 rounded-xl bg-white
        focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300
        text-gray-700 cursor-pointer hover:border-gray-300 transition-all
        ${className}
      `}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  // ✅ NEW: dialog state for "kicked out" reason
  const [dialog, setDialog] = useState({ open: false, type: "info", title: "", message: "" });

  const [pages, setPages] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newActive, setNewActive] = useState(true);
  const [newVisible, setNewVisible] = useState(true);
  const [pendingDeletePageId, setPendingDeletePageId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
  const [selectedTypes, setSelectedTypes] = useState(() => ({
    hero: true,
    features: true,
    testimonials: true,
    gallery: false,
    cta: true,
    categorysection: false,
    locationsection: false,
    serviceSection: false,
  }));
  const [busyId, setBusyId] = useState("");
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const [dict, setDict] = useState(null);
  const [loadingLang, setLoadingLang] = useState(true);

  // ✅ NEW: show dialog if redirected with state
  useEffect(() => {
    const payload = location.state?.dialog;
    if (!payload) return;

    setDialog({ open: true, ...payload });

    // clear state so refresh does not show again
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    let ignore = false;

    async function loadLanguage() {
      try {
        setLoadingLang(true);
        const res = await fetch(`${API_BASE}/api/language/${lang}`);
        const data = await res.json();

        if (!ignore) {
          setDict(data || {});
        }
      } catch (err) {
        console.error("Failed to load language:", err);
        if (!ignore) setDict({});
      } finally {
        if (!ignore) setLoadingLang(false);
      }
    }

    loadLanguage();
    return () => {
      ignore = true;
    };
  }, [lang, API_BASE]);

  const t = (key, fallback = "") => {
    const value = key.split(".").reduce((acc, part) => acc?.[part], dict);
    return value ?? fallback;
  };

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        const loaded = await pagesApi.list();
        if (ignore) return;
        setPages(loaded || []);
      } catch (e) {
        console.error("Failed to load pages:", e);
        if (!ignore) setPages([]);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, []);

  const stats = useMemo(() => {
    const total = pages.length;
    const active = pages.filter((p) => readActive(p)).length;
    const visible = pages.filter((p) => readVisible(p)).length;
    const hidden = total - visible;
    return { total, active, visible, hidden };
  }, [pages]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = pages;

    if (q) {
      out = out.filter((p) =>
        `${p?.name || ""} ${p?.slug || ""}`.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== "all") {
      out = out.filter((p) => (statusFilter === "active" ? readActive(p) : !readActive(p)));
    }

    if (visibilityFilter !== "all") {
      out = out.filter((p) => (visibilityFilter === "visible" ? readVisible(p) : !readVisible(p)));
    }

    const sorter = {
      name: (a, b) => String(a?.name || "").localeCompare(String(b?.name || "")),
      slug: (a, b) => String(a?.slug || "").localeCompare(String(b?.slug || "")),
      sections: (a, b) => (b?.sections?.length || 0) - (a?.sections?.length || 0),
    }[sortBy];

    return [...out].sort(sorter || (() => 0));
  }, [pages, search, statusFilter, visibilityFilter, sortBy]);

  const buildSectionsFromTypes = (types) => {
    return (types || []).map((type) => {
      const tpl = sectionTemplates?.[type]?.template1;
      return {
        id: `section-${Date.now()}-${type}-${Math.random().toString(16).slice(2)}`,
        type,
        template: "template1",
        enabled: true,
        config: deepClone(tpl?.config || {}),
        style: { bgColor: "" },
      };
    });
  };

  const handleToggleActive = async (page) => {
    const currentActive = readActive(page);
    const currentVisibleRaw = readVisibleRaw(page);

    const nextActive = !currentActive;
    const nextVisibleRaw = nextActive ? currentVisibleRaw : false;

    setBusyId(page.id);
    setPages((prev) =>
      prev.map((p) => (p.id === page.id ? { ...p, active: nextActive, visible: nextVisibleRaw } : p))
    );

    try {
      const patch = nextActive ? { active: true } : { active: false, visible: false };
      const updated = await pagesApi.updateStatus(page.id, patch);
      setPages((prev) => prev.map((p) => (p.id === page.id ? updated : p)));
    } catch (e) {
      setPages((prev) =>
        prev.map((p) =>
          p.id === page.id ? { ...p, active: currentActive, visible: currentVisibleRaw } : p
        )
      );
    } finally {
      setBusyId("");
    }
  };

  const handleToggleVisible = async (page) => {
    if (!readActive(page)) return;
    const current = readVisible(page);
    const next = !current;

    setBusyId(page.id);
    setPages((prev) => prev.map((p) => (p.id === page.id ? { ...p, visible: next } : p)));

    try {
      const updated = await pagesApi.updateStatus(page.id, { visible: next });
      setPages((prev) => prev.map((p) => (p.id === page.id ? updated : p)));
    } catch (e) {
      setPages((prev) => prev.map((p) => (p.id === page.id ? { ...p, visible: current } : p)));
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = (pageId) => {
    const page = pages.find((p) => p.id === pageId);
    if (!page) return;

    if (pages.length === 1) {
      alert("You cannot delete the last page.");
      return;
    }

    setDeleteError("");
    setPendingDeletePageId(pageId);
  };

  const confirmDeletePage = async () => {
    const pageId = pendingDeletePageId;
    if (!pageId) return;

    const page = pages.find((p) => p.id === pageId);
    if (!page) {
      setPendingDeletePageId(null);
      return;
    }

    try {
      setBusyId(pageId);
      setDeleteError("");
      await pagesApi.remove(pageId);
      setPages((prev) => prev.filter((p) => p.id !== pageId));
      setPendingDeletePageId(null);
    } catch (e) {
      setDeleteError(e?.message || "Failed to delete page");
    } finally {
      setBusyId("");
    }
  };

  const closeDeleteModal = () => {
    if (busyId === pendingDeletePageId) return;
    setPendingDeletePageId(null);
    setDeleteError("");
  };

  const handleCreate = async () => {
    const name = newName.trim() || "New Page";
    const slug = slugify(newSlug || name);

    if (!slug) return alert("Please enter a valid slug.");
    if (isReservedSlug(slug)) return alert("This slug is reserved. Choose a different one.");
    if (pages.some((p) => p.slug === slug)) return alert("This slug already exists.");

    const chosenTypes = SECTION_TYPES.filter((s) => selectedTypes[s.type]).map((s) => s.type);
    const sections = buildSectionsFromTypes(chosenTypes);

    const footerTpl = sectionTemplates?.footersection?.template1;
    const footer = {
      enabled: true,
      template: "template1",
      config: deepClone(footerTpl?.config || {}),
      style: { ...(deepClone(footerTpl?.style || {})), bgColor: footerTpl?.style?.bgColor ?? "" },
    };

    const navTpl = sectionTemplates?.navsection?.template1;
    const nav = {
      enabled: true,
      template: "template1",
      config: deepClone(navTpl?.config || {}),
      style: { ...(deepClone(navTpl?.style || {})), bgColor: navTpl?.style?.bgColor ?? "" },
    };

    const payload = {
      name,
      slug,
      sections,
      footer,
      nav,
      active: !!newActive,
      visible: !!newVisible,
    };

    try {
      const created = await pagesApi.create(payload);
      setPages((prev) => [created, ...prev]);
      setShowCreate(false);
      setNewName("");
      setNewSlug("");
      setNewActive(true);
      setNewVisible(true);
      navigate(`/admin/pages/${created.id}`);
    } catch (e) {
      alert(e?.message || "Failed to create page");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const pendingDeletePage = pages.find((p) => p.id === pendingDeletePageId) || null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ✅ dialog */}
      <InfoDialog
        open={dialog.open}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        onClose={() => setDialog((p) => ({ ...p, open: false }))}
      />

      {/* ✅ FIXED MOBILE HEADER */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 sm:py-0 sm:h-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-gray-900 to-gray-700 text-white font-bold text-lg shadow-lg shrink-0">
                SB
              </div>

              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight truncate">
                  SmartBusiness Admin
                </h1>
                <p className="hidden sm:block text-sm text-gray-500 leading-tight truncate">
                  Manage your business pages
                </p>
              </div>
            </div>

            <div className="w-full sm:w-auto flex items-center gap-2 sm:gap-3">
              <Button
                variant="primary"
                size="sm"
                icon="+"
                onClick={() => setShowCreate(true)}
                className="flex-1 sm:flex-none"
              >
                New Page
              </Button>

              <Button
                variant="ghost"
                size="sm"
                icon={<LogOut className="h-4 w-4" />}
                onClick={handleLogout}
                className="flex-1 sm:flex-none text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
              >
                Logout
              </Button>

              <Button
                variant="secondary"
                size="sm"
                icon="🌍"
                onClick={() => navigate("/admin/language")}
                className="flex-1 sm:flex-none"
              >
                Translations
              </Button>

            </div>
          </div>
        </div>
      </header>

      {/* rest of your component is unchanged */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard title="Total Pages" value={stats.total} hint="All created pages" icon="📊" color="gray" />
          <StatCard title="Active" value={stats.active} hint="Currently enabled" icon="✅" color="green" />
          <StatCard title="Visible" value={stats.visible} hint="Publicly accessible" icon="👁️" color="blue" />
          <StatCard title="Hidden" value={stats.hidden} hint="Not visible to public" icon="🔒" color="amber" />
        </div>

        <div className="mb-6 space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search pages by name or slug..."
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: "all", label: "All Status" },
                  { value: "active", label: "Active Only" },
                  { value: "inactive", label: "Inactive Only" },
                ]}
              />
              <Select
                value={visibilityFilter}
                onChange={(e) => setVisibilityFilter(e.target.value)}
                options={[
                  { value: "all", label: "All Visibility" },
                  { value: "visible", label: "Visible Only" },
                  { value: "hidden", label: "Hidden Only" },
                ]}
              />
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={[
                  { value: "name", label: "Sort by Name" },
                  { value: "slug", label: "Sort by Slug" },
                  { value: "sections", label: "Sort by Sections" },
                ]}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <Badge variant="soft-gray" size="sm">
              {filtered.length} {filtered.length === 1 ? "page" : "pages"} shown
            </Badge>
            <span className="text-gray-300">•</span>
            <span className="text-gray-500">Tip: Use short, descriptive slugs for better URLs</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-6 py-4 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="lg:col-span-4">Page</div>
            <div className="lg:col-span-2">Status</div>
            <div className="lg:col-span-2">Visibility</div>
            <div className="lg:col-span-1">Sections</div>
            <div className="lg:col-span-3 text-right">Actions</div>
          </div>

          <div className="divide-y divide-gray-200">
            {filtered.map((p) => {
              const isActive = readActive(p);
              const isVisible = readVisible(p);
              const sectionsCount = p?.sections?.length || 0;

              return (
                <div key={p.id} className="px-6 py-4 hover:bg-gray-50/50 transition-colors">
                  <div className="lg:hidden space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${isActive ? "bg-green-50" : "bg-gray-50"
                            }`}
                        >
                          <span className="text-lg">{isActive ? "✅" : "⏸️"}</span>
                        </div>

                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{p?.name || "Untitled"}</h3>
                          <p className="text-sm text-gray-500 truncate">/{p?.slug || ""}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/pages/${p.id}`)}>
                          Edit
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Active</span>
                        <Switch checked={isActive} onChange={() => handleToggleActive(p)} disabled={busyId === p.id} />
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Visible</span>
                        <Switch
                          checked={isVisible}
                          onChange={() => handleToggleVisible(p)}
                          disabled={busyId === p.id || !isActive}
                        />
                      </div>

                      <Badge variant="soft-gray" size="sm">
                        {sectionsCount} sections
                      </Badge>
                    </div>
                  </div>

                  <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
                    <div className="lg:col-span-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-lg ${isActive ? "bg-green-50" : "bg-gray-50"
                            }`}
                        >
                          <span className="text-lg">{isActive ? "✅" : "⏸️"}</span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{p?.name || "Untitled"}</div>
                          <div className="text-sm text-gray-500">/{p?.slug || ""}</div>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-2">
                      <div className="flex items-center gap-3">
                        <Badge variant={isActive ? "solid-green" : "soft-gray"} size="sm">
                          {isActive ? "Active" : "Inactive"}
                        </Badge>
                        <Switch checked={isActive} onChange={() => handleToggleActive(p)} disabled={busyId === p.id} />
                      </div>
                    </div>

                    <div className="lg:col-span-2">
                      <div className="flex items-center gap-3">
                        <Badge variant={isVisible ? "solid-blue" : "soft-gray"} size="sm">
                          {isVisible ? "Visible" : "Hidden"}
                        </Badge>
                        <Switch
                          checked={isVisible}
                          onChange={() => handleToggleVisible(p)}
                          disabled={busyId === p.id || !isActive}
                        />
                      </div>
                    </div>

                    <div className="lg:col-span-1">
                      <span className="font-medium text-gray-900">{sectionsCount}</span>
                    </div>

                    <div className="lg:col-span-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon="🔍"
                          onClick={() => window.open(`/${p.slug}`, "_blank")}
                          title="Preview page"
                        >
                          <span className="hidden xl:inline">Preview</span>
                        </Button>

                        <Button variant="secondary" size="sm" icon="✏️" onClick={() => navigate(`/admin/pages/${p.id}`)}>
                          Edit
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          icon="🗑️"
                          onClick={() => handleDelete(p.id)}
                          disabled={busyId === p.id}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          {busyId === p.id ? "..." : "Delete"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="px-6 py-16 text-center">
                <div className="text-5xl mb-4">📭</div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No pages found</h3>
                <p className="text-sm text-gray-500 mb-4">Try adjusting your filters or create a new page</p>
                <Button variant="primary" onClick={() => setShowCreate(true)}>
                  Create New Page
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* keep your existing create modal + delete modal exactly as-is */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-4">Create New Page</h2>

            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Page Name"
              className="w-full border p-2 rounded mb-3"
            />

            <input
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="Slug"
              className="w-full border p-2 rounded mb-4"
            />

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>

              <Button variant="primary" onClick={handleCreate}>
                Create
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={pendingDeletePageId !== null}
        title={t("adminDashboard.deleteModal.title", "Delete page?")}
        message={
          pendingDeletePage
            ? t(
              "adminDashboard.deleteModal.messageWithName",
              `Are you sure you want to delete "${pendingDeletePage.name}"? This action cannot be undone.`
            )
            : t(
              "adminDashboard.deleteModal.message",
              "Are you sure you want to delete this page? This action cannot be undone."
            )
        }
        confirmText={t("adminDashboard.deleteModal.confirm", "Delete")}
        cancelText={t("adminDashboard.deleteModal.cancel", "Cancel")}
        loading={busyId === pendingDeletePageId}
        onClose={closeDeleteModal}
        onConfirm={confirmDeletePage}
      />
    </div>
  );
}